import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

/**
 * Custom hook to easily access LaunchDarkly feature flags
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { 'my-feature-flag': isMyFeatureEnabled } = useFeatureFlag();
 *   
 *   if (isMyFeatureEnabled) {
 *     return <NewFeature />;
 *   }
 *   return <OldFeature />;
 * };
 * ```
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const flags = useFeatureFlag();
 *   const isEnabled = flags['my-feature-flag'] ?? false;
 *   
 *   return <div>Feature is {isEnabled ? 'enabled' : 'disabled'}</div>;
 * };
 * ```
 */
export const useFeatureFlag = () => {
  const flags = useFlags();
  return flags;
};

/**
 * Custom hook to track custom events in LaunchDarkly
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const trackEvent = useFeatureFlagTracking();
 *   
 *   const handleClick = () => {
 *     trackEvent('button-clicked', { buttonName: 'submit' });
 *   };
 *   
 *   return <button onClick={handleClick}>Submit</button>;
 * };
 * ```
 */
export const useFeatureFlagTracking = () => {
  const ldClient = useLDClient();
  
  return (eventName: string, data?: Record<string, any>) => {
    if (ldClient) {
      ldClient.track(eventName, data);
    }
  };
};

